import React, { useEffect, useState } from "react";
import { Typography, Skeleton, Empty } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiBookOpen, FiClock } from "react-icons/fi";
import { Get } from "../../../config/api/get";
import { ASSOCIATE_PORTAL, UPLOADS_URL } from "../../../config/constants/api";
import AssociateLayout, {
  getAssociateInitials,
} from "../../../components/AssociateLayout";
import "./associate-dashboard.css";

const { Title } = Typography;

function lessonCount(course) {
  if (Array.isArray(course?.lectures)) {
    return course.lectures.length;
  }
  return 0;
}

function AssociateDashboard() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const reduxUser = useSelector((state) => state.user.userData);
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [meRes, coursesRes] = await Promise.all([
        Get(ASSOCIATE_PORTAL.me, token),
        Get(ASSOCIATE_PORTAL.courses, token),
      ]);
      if (meRes?.status) {
        setPortal(meRes.data);
      }
      if (coursesRes?.status) {
        setCourses(coursesRes.data?.courses || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const associate = portal?.associate || reduxUser;
  const ownerCoach = portal?.ownerCoach;
  const fullName = associate
    ? `${associate.firstName || ""} ${associate.lastName || ""}`.trim()
    : "Associate";
  const initials = getAssociateInitials(associate);
  const courseCount = portal?.grantedCourseCount ?? courses.length;

  return (
    <AssociateLayout footer={false}>
      <div className="associate-portal-page">
        <div className="associate-portal-page__inner">
          {loading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <>
              <section className="associate-welcome-card">
                <div className="associate-welcome-card__main">
                  <div className="associate-welcome-avatar" aria-hidden>
                    {initials}
                  </div>
                  <div>
                    <Title level={2} className="associate-welcome-card__title fontFamily1">
                      Welcome, {fullName}
                    </Title>
                    <p className="associate-welcome-card__line fontFamily1">
                      <strong>Position:</strong> {associate?.position || "—"}
                    </p>
                    {ownerCoach ? (
                      <p className="associate-welcome-card__line fontFamily1">
                        <strong>Trainer:</strong> {ownerCoach.firstName}{" "}
                        {ownerCoach.lastName}
                        {ownerCoach.tagLine ? ` — ${ownerCoach.tagLine}` : ""}
                      </p>
                    ) : null}
                    <span className="associate-welcome-badge fontFamily1">
                      {courseCount} assigned course{courseCount === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="associate-welcome-card__decor" aria-hidden>
                  <FiBookOpen />
                </div>
              </section>

              {courses.length === 0 ? (
                <Empty description="No courses assigned yet. Contact your trainer." />
              ) : (
                <div className="associate-courses-grid">
                  {courses.map((course) => {
                    const lessons = lessonCount(course);
                    const duration = course.duration?.trim() || "—";

                    return (
                      <article
                        key={course._id}
                        className="associate-course-card"
                        onClick={() =>
                          navigate(`/associate/courses/${course._id}`)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            navigate(`/associate/courses/${course._id}`);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="associate-course-card__media">
                          {course.image ? (
                            <img
                              src={`${UPLOADS_URL}/${course.image}`}
                              alt={course.title}
                            />
                          ) : null}
                        </div>
                        <div className="associate-course-card__body">
                          <h3 className="associate-course-card__title fontFamily1">
                            {course.title}
                          </h3>
                          <p className="associate-course-card__desc fontFamily1">
                            {course.description?.trim() ||
                              course.courseCode ||
                              "No description"}
                          </p>
                          <div className="associate-course-card__meta fontFamily1">
                            <span>
                              <FiBookOpen aria-hidden />
                              {lessons} Lesson{lessons === 1 ? "" : "s"}
                            </span>
                            <span>
                              <FiClock aria-hidden />
                              {duration}
                            </span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AssociateLayout>
  );
}

export default AssociateDashboard;
